import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useCallback, useMediaTypes } from 'sb-hooks'
import cx from 'classnames'
import { array, constants } from 'common/helpers'

import Cell from '../Cell/Cell'
import FillCell from '../FillCell/FillCell'

import s from './Grid.scss'


const Grid = ({ children, id, className, columns, gutter, alignItems, noWrap }) => {
  const alignItemsClassName = {
    center: s['alignItems-center'],
  }[alignItems]

  const gridClassName = cx(s.grid, alignItemsClassName, className, {
    [s.noWrap]: noWrap,
  })

  const mediaTypes = useMediaTypes()

  const getGrid = useCallback((columnsCount, gutter) => (
    <div id={id} className={cx(gridClassName, { [s[`gutter_${gutter}`]]: gutter })} role="list">
      {
        React.Children.map(children, (child, index) => {
          if (child) {
            const cellProps = {
              columns: columnsCount,
              gutter,
            }

            if (child.type === FillCell) {
              return React.cloneElement(child, { ...cellProps, index })
            }
            if (child.type === Cell) {
              return React.cloneElement(child, cellProps)
            }

            return (
              <Cell {...cellProps}>
                {child}
              </Cell>
            )
          }
        })
      }
    </div>
  ), [ children, gridClassName, id ])

  if (typeof columns === 'number') {
    return getGrid(columns, gutter)
  }

  if (typeof columns === 'object') {
    const mediaType = Object.keys(columns)
      .find((key) => mediaTypes[key])

    if (!mediaType) {
      console.warn(`No active media type found`)
      return null
    }

    const mediaGutter = gutter && typeof gutter === 'object' ? gutter[mediaType] : gutter

    return (
      <Fragment>
        {getGrid(columns[mediaType], mediaGutter)}
      </Fragment>
    )
  }

  return null
}

const columnsRange = PropTypes.oneOf(array.range(1, 12))
const gutterRange = PropTypes.oneOf([ '8', '12', '16', '24', '32', 8, 12, 16, 24, 32 ])

Grid.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  className: PropTypes.string,
  columns: PropTypes.oneOfType([
    columnsRange.isRequired,
    PropTypes.shape(
      constants.mediaList.reduce((result, media) => ({
        ...result,
        [media]: columnsRange,
      }), {}),
    ).isRequired,
  ]),
  gutter: PropTypes.oneOfType([
    gutterRange.isRequired,
    PropTypes.shape(
      constants.mediaList.reduce((result, media) => ({
        ...result,
        [media]: gutterRange,
      }), {}),
    ).isRequired,
  ]),
  alignItems: PropTypes.oneOf([ 'center' ]),
  noWrap: PropTypes.bool,
}

Grid.defaultProps = {
  columns: 1,
  noWrap: false,
}


export default Grid
